import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Divider,
  Flex,
  Grid,
  Icon,
  Scroll,
  Switch,
  Typography,
} from "@smartfaster-ui/components";
import {
  BUTTON_ICON_NAMES,
  BUTTON_LABEL_KEYS,
  IMPLEMENTED_PANELS,
} from "./context/constants";
import { useInteriorDesigner } from "./context/InteriorDesignerContext";
import { getAvailableChips, hasHistory } from "./context/selectors";
import type { PanelId } from "./context/types";
import { useFurnitureToggle } from "./hooks/useFurnitureToggle";
import { cardGridStyle, optionCardStyle } from "./CompactControls.css";

/**
 * Compact (mobile) controls shown in the bottom half of the split layout.
 * A distinct UI from the desktop overlay (WayfindingControls): a back/history
 * row, a scrollable 2-column grid of design-option cards, and a furniture
 * toggle row.
 */
export function CompactControls() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state, dispatch } = useInteriorDesigner();

  const isGenerating = state.generation.status === "loading";
  const showHistory = hasHistory({ state });
  const availableChips = getAvailableChips({ state });

  const {
    isVisible: showFurnitureToggle,
    canToggle: canToggleFurniture,
    furnitureVisible,
    toggle: handleFurnitureToggle,
  } = useFurnitureToggle();

  const handleBackClick = () => navigate(-1);

  const handleHistoryClick = () =>
    dispatch({ type: "PANEL_OPENED", panel: "history" });

  const handleCardClick = (panelId: PanelId) => {
    if (isGenerating) {
      return;
    }
    dispatch({ type: "PANEL_OPENED", panel: panelId });
  };

  return (
    <Flex
      width="fill"
      height="fill"
      direction="column"
      paddingX="sm"
      paddingY="sm"
      gap="sm"
    >
      <Flex justify="between" align="center" gap="sm">
        <Button
          variant="filled"
          color="surfaceNeutralSecondary"
          size="md"
          leading={<Icon name="ArrowLeft01Icon" />}
          onClick={handleBackClick}
        >
          {t("interiorDesigner.wayfinding.backShort")}
        </Button>
        <Button
          variant="filled"
          color="surfaceNeutralSecondary"
          size="md"
          leading={<Icon name="Clock01Icon" />}
          onClick={handleHistoryClick}
          disabled={isGenerating || !showHistory}
        >
          {t("interiorDesigner.wayfinding.history")}
        </Button>
      </Flex>

      <Divider />

      <Scroll grow hideScrollbar>
        <Grid className={cardGridStyle} gap="sm">
          {availableChips.map((panelId) => (
            <Card
              key={panelId}
              as="button"
              interactive
              variant="outlined"
              paddingX="responsiveLg"
              paddingY="xs"
              className={optionCardStyle}
              disabled={isGenerating || !IMPLEMENTED_PANELS.has(panelId)}
              onClick={() => handleCardClick(panelId)}
            >
              <Flex
                width="fill"
                height="fill"
                direction="row"
                align="center"
                gap="sm"
              >
                <Icon name={BUTTON_ICON_NAMES[panelId]} />
                <Typography variant="bodyMd">
                  {t(BUTTON_LABEL_KEYS[panelId])}
                </Typography>
              </Flex>
            </Card>
          ))}
        </Grid>
      </Scroll>

      {showFurnitureToggle && (
        <>
          <Divider />
          <Flex justify="between" align="center" gap="sm">
            <Flex align="center" gap="sm">
              <Icon name="Cabinet01Icon" color="foregroundPrimary" />
              <Typography variant="bodyMd">
                {t("interiorDesigner.furniture.label")}
              </Typography>
            </Flex>
            <Switch
              size="sm"
              variant="primary"
              checked={canToggleFurniture && furnitureVisible}
              disabled={isGenerating || !canToggleFurniture}
              onCheckedChange={handleFurnitureToggle}
            />
          </Flex>
        </>
      )}
    </Flex>
  );
}
